import React, { useState } from 'react';
import { useStore } from '../store';
import { SearchModal } from './SearchModal';
import { useAuth } from './AuthProvider';

export function Toolbar() {
  const [activeTool, setActiveTool] = useState<string>('select');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const addNode = useStore((state) => state.addNode);
  const undo = useStore((state) => state.undo);
  const redo = useStore((state) => state.redo);
  const exportToJSON = useStore((state) => state.exportToJSON);
  const exportToPNG = useStore((state) => state.exportToPNG);
  const clearCanvas = useStore((state) => state.clearCanvas);

  const handleToolSelect = (tool: string) => {
    setActiveTool(tool);
  };

  const handleAddNode = (type: 'task' | 'note' | 'milestone' | 'text' | 'rectangle' | 'circle' | 'diamond') => {
    setActiveTool(type);
    const canvasEl = document.getElementById('canvas');
    if (canvasEl) {
      const rect = canvasEl.getBoundingClientRect();
      const x = rect.width / 2 - 140;
      const y = rect.height / 2 - 50;
      addNode(x, y, type);
      setActiveTool('select');
    }
  };

  const handleSearch = () => {
    setIsSearchModalOpen(true);
  };

  const handleExportJSON = () => {
    const json = exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `excaliplan-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    if (confirm('Are you sure you want to clear the entire canvas?')) {
      clearCanvas();
    }
  };

  return (
    <>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />

      {/* Main toolbar at bottom center */}
      <div className="toolbar-main">
        <button
          className={`toolbar-btn ${activeTool === 'select' ? 'active' : ''}`}
          onClick={() => handleToolSelect('select')}
          title="Select"
          data-tool="select"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'task' ? 'active' : ''}`}
          onClick={() => handleAddNode('task')}
          title="Add Task"
          data-tool="task"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'note' ? 'active' : ''}`}
          onClick={() => handleAddNode('note')}
          title="Add Note"
          data-tool="note"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'milestone' ? 'active' : ''}`}
          onClick={() => handleAddNode('milestone')}
          title="Add Milestone"
          data-tool="milestone"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3 7h7l-5.5 5 2 7L12 17.5 5.5 21l2-7L2 9h7z"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'text' ? 'active' : ''}`}
          onClick={() => handleAddNode('text')}
          title="Add Text"
          data-tool="text"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
          </svg>
        </button>
        <div className="toolbar-divider"></div>
        <button
          className={`toolbar-btn ${activeTool === 'rectangle' ? 'active' : ''}`}
          onClick={() => handleAddNode('rectangle')}
          title="Add Rectangle"
          data-tool="rectangle"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="5" width="18" height="14" rx="1"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'circle' ? 'active' : ''}`}
          onClick={() => handleAddNode('circle')}
          title="Add Circle"
          data-tool="circle"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9"/>
          </svg>
        </button>
        <button
          className={`toolbar-btn ${activeTool === 'diamond' ? 'active' : ''}`}
          onClick={() => handleAddNode('diamond')}
          title="Add Diamond"
          data-tool="diamond"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="8.5" y="8.5" width="7" height="7" rx="1" transform="rotate(45 12 12)"/>
          </svg>
        </button>
        <div className="toolbar-divider"></div>
        <button
          className="toolbar-btn"
          onClick={handleSearch}
          title="Search"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </button>
        <button
          className="toolbar-btn"
          onClick={handleExportJSON}
          title="Export JSON"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <button
          className="toolbar-btn"
          onClick={exportToPNG}
          title="Export PNG"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </button>
        <button
          className="toolbar-btn"
          onClick={handleClear}
          title="Clear Canvas"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
      </div>

      {/* Actions toolbar at bottom left */}
      <div className="toolbar-actions">
        <button
          className="toolbar-btn"
          onClick={undo}
          title="Undo"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7v6h6"/>
            <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
          </svg>
        </button>
        <button
          className="toolbar-btn"
          onClick={redo}
          title="Redo"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 7v6h-6"/>
            <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"/>
          </svg>
        </button>
      </div>

      {/* User menu at top right */}
      <div className="user-menu">
        <button
          className="toolbar-btn user-btn"
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          title={user?.email || 'User'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
        {isUserMenuOpen && (
          <div className="user-dropdown">
            <div className="user-info">
              <div className="user-email">{user?.email}</div>
              {user?.full_name && <div className="user-name">{user.full_name}</div>}
            </div>
            <button
              className="logout-btn"
              onClick={() => {
                logout();
                setIsUserMenuOpen(false);
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </>
  );
}
